import React, { useEffect, useMemo, useState } from "react"
import { CalendarPlus, Check, CheckCircle2, Clock3, Copy, Cookie, Download, MapPin, Radio, Trophy, X } from "lucide-react"
import { api, json } from "../api"
import { useParams } from "../router"
import { Button, ErrorNotice } from "../ui"

export function PublicTeamPage() {
  const { slug } = useParams("/pages/:slug")
  const [team, setTeam] = useState(null)
  const [error, setError] = useState(null)
  const [signed, setSigned] = useState({})
  const [submitting, setSubmitting] = useState(null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarCopied, setCalendarCopied] = useState(null)

  useEffect(() => { api(`/public/teams/${slug}`).then(setTeam).catch(setError) }, [slug])

  useEffect(() => {
    if (!calendarOpen) return undefined

    const closeOnEscape = event => { if (event.key === "Escape") setCalendarOpen(false) }
    document.addEventListener("keydown", closeOnEscape)
    return () => document.removeEventListener("keydown", closeOnEscape)
  }, [calendarOpen])

  const events = useMemo(() => team ? [
    ...team.games.map(game => ({ ...game, date: game.game_date, kind: "game" })),
    ...team.practices.map(practice => ({ ...practice, date: practice.practice_date, kind: "practice" })),
  ].sort((a, b) => new Date(a.date) - new Date(b.date)) : [], [team])

  async function signup(event, gameId) {
    event.preventDefault()
    setSubmitting(gameId)
    setError(null)
    try {
      const volunteer = await api(`/public/games/${gameId}/snack_signups`, json("POST", { snack_signup: Object.fromEntries(new FormData(event.currentTarget)) }))
      setSigned(current => ({ ...current, [gameId]: volunteer }))
    } catch (caught) { setError(caught) } finally { setSubmitting(null) }
  }

  async function copyCalendarUrl(url) {
    let copied = false
    try {
      await navigator.clipboard.writeText(url)
      copied = true
    } catch (_error) {
      const input = document.querySelector("#team-calendar-url")
      input?.focus()
      input?.select()
      copied = document.execCommand?.("copy") || false
    }
    setCalendarCopied(copied ? "copied" : "manual")
    window.setTimeout(() => setCalendarCopied(null), 2200)
  }

  if (!team) return <main className="public-team-loading"><span className="public-team-loading__ball" aria-hidden="true" /><ErrorNotice error={error} /><p>Loading the team schedule…</p></main>

  const now = Date.now()
  const upcoming = events.filter(item => new Date(item.date).getTime() >= now)
  const past = events.filter(item => new Date(item.date).getTime() < now).reverse().slice(0, 5)
  const nextEvent = upcoming[0]
  const remaining = upcoming.slice(1)
  const upcomingGames = upcoming.filter(item => item.kind === "game").length
  const upcomingPractices = upcoming.filter(item => item.kind === "practice").length
  const timezone = team.timezone || "America/New_York"
  const theme = teamTheme(team)
  const calendarPath = `/api/calendar/${team.slug}`
  const calendarUrl = `${window.location.origin}${calendarPath}`
  const webcalUrl = calendarUrl.replace(/^https?:/, "webcal:")

  return (
    <main className="public-team-page" style={theme}>
      <header className="public-team-hero">
        <span className="public-team-hero__diamond" aria-hidden="true" />
        <div className="public-team-shell public-team-hero__inner">
          <div className="public-team-brandbar">
            <a className="public-team-brand" href="/landing"><img src="/brand/baseball-hub-logo.png" alt="" /><strong>Baseball Hub</strong></a>
            <p><Radio size={16} aria-hidden="true" /> Live team schedule</p>
          </div>
          <div className="public-team-identity">
            <TeamLogo team={team} />
            <div className="public-team-identity__copy">
              <p className="public-team-kicker">{team.season}</p>
              <h1>{team.name}</h1>
              <p>Coach {team.coach.first_name} {team.coach.last_name}</p>
            </div>
            <button type="button" className="public-calendar-cta" onClick={() => setCalendarOpen(true)}><CalendarPlus size={16} aria-hidden="true" /> Subscribe to team calendar</button>
          </div>
          <dl className="public-team-stats">
            <div><dt>Next event</dt><dd>{nextEvent ? formatShortDate(nextEvent.date, timezone) : "Schedule open"}</dd></div>
            <div><dt>Games ahead</dt><dd>{upcomingGames}</dd></div>
            <div><dt>Practices ahead</dt><dd>{upcomingPractices}</dd></div>
          </dl>
        </div>
      </header>

      <div className="public-team-shell public-team-content">
        <ErrorNotice error={error} />

        {nextEvent ? (
          <section className={`public-next-event ${nextEvent.kind === "practice" ? "public-next-event--practice" : ""}`}>
            <div className="public-next-event__main">
              <div className="public-section-heading">
                <p className="public-team-kicker">Next game</p>
                <h2>{nextEvent.kind === "game" ? `${team.name} vs ${nextEvent.opponent}` : `${team.name} practice`}</h2>
              </div>
              <EventDetails item={nextEvent} timezone={timezone} featured />
            </div>
            {nextEvent.kind === "game" && team.enable_snack_schedule ? <SnackDuty item={nextEvent} volunteer={signed[nextEvent.id] || nextEvent.snack_signups?.[0]} onSignup={signup} submitting={submitting === nextEvent.id} featured /> : (
              <div className="public-calendar-note"><CalendarPlus size={16} aria-hidden="true" /><div><strong>Never miss an update.</strong><p>Add the team calendar once and schedule changes will stay close at hand.</p></div></div>
            )}
          </section>
        ) : (
          <section className="public-schedule-empty"><Trophy size={24} aria-hidden="true" /><div><h2>The next game is still being drawn up.</h2><p>Check back soon for new games and practices.</p></div></section>
        )}

        {remaining.length ? (
          <section className="public-schedule-section">
            <div className="public-section-heading">
              <p className="public-team-kicker">On deck</p>
              <h2>Coming up</h2>
              <p>Everything the team has ahead, in game-day order.</p>
            </div>
            <div className="public-event-list" role="list">
              {remaining.map(item => <EventCard key={`${item.kind}-${item.id}`} item={item} timezone={timezone} snacksEnabled={team.enable_snack_schedule} volunteer={signed[item.id] || item.snack_signups?.[0]} onSignup={signup} submitting={submitting === item.id} />)}
            </div>
          </section>
        ) : null}

        {past.length ? (
          <section className="public-schedule-section public-schedule-section--past">
            <div className="public-section-heading">
              <p className="public-team-kicker">In the books</p>
              <h2>Recent team events</h2>
            </div>
            <div className="public-event-list" role="list">
              {past.map(item => <EventCard key={`${item.kind}-${item.id}`} item={item} timezone={timezone} past />)}
            </div>
          </section>
        ) : null}
      </div>

      <footer className="public-team-footer">
        <div className="public-team-shell"><p><strong>Baseball Hub</strong> keeps the whole team ready for the next pitch.</p><a href="/landing">Build your team hub</a></div>
      </footer>

      {calendarOpen ? (
        <div className="public-calendar-dialog__backdrop" onMouseDown={event => { if (event.target === event.currentTarget) setCalendarOpen(false) }}>
          <section className="public-calendar-dialog" role="dialog" aria-modal="true" aria-labelledby="calendar-dialog-title">
            <button type="button" className="public-calendar-dialog__close" onClick={() => setCalendarOpen(false)} aria-label="Close calendar subscription instructions"><X size={20} aria-hidden="true" /></button>
            <div className="public-calendar-dialog__heading">
              <p className="public-team-kicker">Stay in sync</p>
              <h2 id="calendar-dialog-title">Subscribe to the {team.name} calendar</h2>
              <p>Subscribe once to receive games, practices, and schedule changes automatically.</p>
            </div>

            <ol className="public-calendar-steps">
              <li><span>1</span><p><strong>Copy the calendar URL.</strong> Use the button below so you have the live feed address.</p></li>
              <li><span>2</span><p><strong>Open your calendar app.</strong> Look for “Add calendar,” “Subscribe to calendar,” or “From URL.”</p></li>
              <li><span>3</span><p><strong>Paste the URL and save.</strong> Your calendar app will check the feed for future changes.</p></li>
            </ol>

            <div className="public-calendar-url">
              <label htmlFor="team-calendar-url">Calendar subscription URL</label>
              <div>
                <input id="team-calendar-url" value={calendarUrl} readOnly onFocus={event => event.currentTarget.select()} />
                <button type="button" onClick={() => copyCalendarUrl(calendarUrl)} aria-live="polite">
                  {calendarCopied ? <Check size={17} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
                  {calendarCopied === "copied" ? "Copied" : calendarCopied === "manual" ? "Press Ctrl/⌘ C" : "Copy URL"}
                </button>
              </div>
            </div>

            <div className="public-calendar-dialog__actions">
              <a className="public-calendar-dialog__primary" href={webcalUrl}><CalendarPlus size={17} aria-hidden="true" /> Open calendar app</a>
              <a className="public-calendar-dialog__secondary" href={calendarPath} download={`${team.slug}-schedule.ics`}><Download size={17} aria-hidden="true" /> Download .ics file</a>
            </div>

            <p className="public-calendar-dialog__note"><strong>For automatic updates, subscribe with the URL.</strong> Downloading the `.ics` file adds a one-time snapshot that will not update later.</p>
          </section>
        </div>
      ) : null}
    </main>
  )
}

function TeamLogo({ team }) {
  return <div className="public-team-logo">{team.logo_url ? <img src={team.logo_url} alt="" /> : <span>{team.name.slice(0, 2).toUpperCase()}</span>}</div>
}

function EventCard({ item, timezone, past = false, snacksEnabled = false, volunteer, onSignup, submitting }) {
  return (
    <article className={`public-event-card ${past ? "public-event-card--past" : ""}`} role="listitem">
      <DateTile date={item.date} timezone={timezone} />
      <div className="public-event-card__body">
        <p className={`public-event-type public-event-type--${item.kind}`}>{item.kind === "game" ? "Game day" : "Practice"}</p>
        <h3>{item.kind === "game" ? `vs ${item.opponent}` : "Team practice"}</h3>
        <EventDetails item={item} timezone={timezone} />
      </div>
      {!past && item.kind === "game" && snacksEnabled ? <SnackDuty item={item} volunteer={volunteer} onSignup={onSignup} submitting={submitting} /> : null}
    </article>
  )
}

function EventDetails({ item, timezone, featured = false }) {
  const location = item.kind === "game" ? (item.field_number ? `Field ${item.field_number}` : "Field to be announced") : (item.location || "Location to be announced")
  const length = item.kind === "game" ? `${item.innings} innings` : `${item.duration_minutes || 90} minutes`
  return (
    <div className={`public-event-details ${featured ? "public-event-details--featured" : ""}`}>
      <p><Clock3 size={16} aria-hidden="true" /> <span>{formatFullDate(item.date, timezone)} at {formatTime(item.date, timezone)}</span></p>
      <p><MapPin size={16} aria-hidden="true" /> <span>{location}</span></p>
      <p><Trophy size={16} aria-hidden="true" /> <span>{length}</span></p>
    </div>
  )
}

function DateTile({ date, timezone }) {
  const value = new Date(date)
  return <time className="public-date-tile" dateTime={value.toISOString()}><span>{new Intl.DateTimeFormat([], { month: "short", timeZone: timezone }).format(value)}</span><strong>{new Intl.DateTimeFormat([], { day: "numeric", timeZone: timezone }).format(value)}</strong></time>
}

function SnackDuty({ item, volunteer, onSignup, submitting, featured = false }) {
  if (volunteer) return (
    <div className={`public-snack-duty public-snack-duty--covered ${featured ? "public-snack-duty--featured" : ""}`}>
      <CheckCircle2 size={16} aria-hidden="true" />
      <div><strong>Snacks are covered.</strong><p>{volunteer.name} has this game.</p></div>
    </div>
  )

  return (
    <div className={`public-snack-duty ${featured ? "public-snack-duty--featured" : ""}`}>
      <div className="public-snack-duty__heading"><Cookie size={16} aria-hidden="true" /><div><strong>Bring the team snacks.</strong><p>Claim this game and let every family know it is covered.</p></div></div>
      <form className="public-snack-form" onSubmit={event => onSignup(event, item.id)}>
        <div>
          <label htmlFor={`snack-name-${item.id}`}>Your name</label>
          <input id={`snack-name-${item.id}`} name="name" autoComplete="name" required />
        </div>
        <div>
          <label htmlFor={`snack-email-${item.id}`}>Email address</label>
          <input id={`snack-email-${item.id}`} name="email" type="email" autoComplete="email" required />
        </div>
        <Button type="submit" variant="secondary" disabled={submitting}>{submitting ? "Claiming game…" : "I’ll bring snacks"}</Button>
      </form>
    </div>
  )
}

function teamTheme(team) {
  return {
    "--team-primary": team.primary_color || "#1b365d",
    "--team-secondary": team.secondary_color || "#f97316",
    "--team-secondary-ink": readableTextColor(team.secondary_color || "#f97316"),
  }
}

function readableTextColor(hex) {
  const value = hex.replace("#", "")
  const [red, green, blue] = [value.slice(0, 2), value.slice(2, 4), value.slice(4, 6)].map(channel => parseInt(channel, 16))
  return ((red * 299 + green * 587 + blue * 114) / 1000) > 155 ? "#102033" : "#ffffff"
}

function formatShortDate(value, timezone) {
  return new Intl.DateTimeFormat([], { month: "short", day: "numeric", timeZone: timezone }).format(new Date(value))
}

function formatFullDate(value, timezone) {
  return new Intl.DateTimeFormat([], { weekday: "long", month: "long", day: "numeric", timeZone: timezone }).format(new Date(value))
}

function formatTime(value, timezone) {
  return new Intl.DateTimeFormat([], { hour: "numeric", minute: "2-digit", timeZone: timezone }).format(new Date(value))
}
